import Head from "next/head"
import { GetServerSideProps, NextPage } from "next"
import Header from "@modules/Common/components/Header"
import { Container, Row, Col } from "react-bootstrap"
import { Page } from "@styled/dashboard"
import SkillList from "@modules/Skill/components/SkillList"
import styled from "styled-components"
import { SkillsGrid } from "@styled/skills"
import { initializeApollo } from "apollo/client"
import PublicSkillList from "@modules/Skill/components/PublicSkillList"
import { DEFAULT_META_IMAGE_URL } from "@utils/constant"
import { PublicSkillsDocument, PublicSkillsQuery, PublicSkillsQueryVariables } from "@generated/graphql"

const StyledHeader = styled(Row)`
  white-space: nowrap;
  justify-content: center;
  margin: 2em;
  & span {
    background: url(/assets/svg/activities-header.svg);
    background-position: center;
    background-size: contain;
    background-repeat: no-repeat;
    font-weight: 700;
    font-size: 2em;
    text-align: center;
    padding: 1.25em 2em;
    width: auto;
  }
  @media (max-width: 576px) {
    & span {
      font-size: 1.65em;
    }
  }
  @media (max-width: 767px) {
    margin: 2em 0;
  }
`

interface ISkillsIndex {
  isPublic: boolean
  skills: PublicSkillsQuery["publicSkills"]
}

const SkillsIndex: NextPage<ISkillsIndex> = ({ isPublic, skills }) => {

  const title = "Explore Skills - Learn different skills, do it with us | MySkillShaala"
  const description = "We have a variety of different classes to help you learn different skills. Whether you want to learn about technology, arts, STEM or a range of different topics, take a look at our MySkillShaala. Get inspired and try something new!"
  const image = DEFAULT_META_IMAGE_URL

  return (
    <>
      <Head>
        <title>{title}</title>
        <meta name="title" content={title} />
        <meta name="description" content={description} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={`${process.env.NEXT_PUBLIC_SITE_URL}/skills`} />
        <meta property="og:site_name" content={process.env.NEXT_PUBLIC_SITE_URL} />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={description} />
        <meta property="og:image" content={image} />
        <meta property="twitter:card" content="summary_large_image" />
        <meta property="twitter:url" content={`${process.env.NEXT_PUBLIC_SITE_URL}/skills`} />
        <meta property="twitter:title" content={title} />
        <meta property="twitter:description" content={description} />
        <meta property="twitter:image" content={image} />
        <script type="application/ld+json" dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            "itemListElement": [{
              "@type": "ListItem",
              "position": 1,
              "name": "Skills",
              "item": `${process.env.NEXT_PUBLIC_SITE_URL}/skills`
            }]
          })
        }} />
      </Head>
      <Page>
        <Header showCoins={isPublic ? "false" : "true"} />
        <Container fluid>
          <StyledHeader><span>WHAT DO YOU WANT<br />TO LEARN TODAY?</span></StyledHeader>
          <Row className="mt-3">
            <Col lg={{ span: 8, offset: 2 }}>
              <SkillsGrid>
                {isPublic ? <PublicSkillList skills={skills} /> : <SkillList />}
              </SkillsGrid>
            </Col>
          </Row>
        </Container>
      </Page>
    </>
  )
}

export const getServerSideProps: GetServerSideProps = async ({ req: { cookies }, res }) => {
  if (cookies["idToken"]) {
    return {
      props: {
        isPublic: false,
      }
    }
  } else {
    const apolloClient = initializeApollo()
    res.setHeader("Cache-Control", "s-maxage=60, stale-while-revalidate")
    const { data, error } = await apolloClient.query<PublicSkillsQuery, PublicSkillsQueryVariables>({
      query: PublicSkillsDocument,
      variables: {
        skip: 0,
        take: 9
      }
    })
    return {
      props: {
        isPublic: true,
        skills: data.publicSkills,
        initialApolloState: apolloClient.cache.extract(),
      }
    }
  }
}

export default SkillsIndex
